import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch, parseJsonSafe } from '../../utils/api';

// Tarjeta de Configuración → «Conector para Claude»: URL del conector MCP, pasos para
// añadirlo en claude.ai, conexiones autorizadas (con revocación) y últimas acciones.

const fmt = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
};

const ClaudeConnectorCard = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showActivity, setShowActivity] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/mcp/connections', { headers: { Authorization: `Bearer ${token}` } });
      const body = await parseJsonSafe(res);
      if (!res.ok) throw new Error(body.message || 'No se pudo cargar el conector');
      setData(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const copyUrl = async () => {
    if (!data?.url) return;
    try {
      await navigator.clipboard.writeText(data.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      window.prompt('Copia la URL del conector:', data.url);
    }
  };

  const revoke = async (conn) => {
    if (!window.confirm(`¿Revocar la conexión de «${conn.client}» (${conn.userEmail})? Claude dejará de poder acceder hasta que se vuelva a conectar.`)) return;
    try {
      const res = await apiFetch(`/api/mcp/connections/${encodeURIComponent(conn.grantId)}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const body = await parseJsonSafe(res);
      if (!res.ok) throw new Error(body.message || 'No se pudo revocar');
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const connections = data?.connections || [];
  const activity = data?.activity || [];

  return (
    <div className="card shadow-sm mt-4">
      <div className="card-body d-flex flex-column">
        <h5 className="card-title">Conector para Claude</h5>
        <p className="card-text text-muted small mb-2">
          Conecta la app a Claude (web, escritorio y móvil) como conector personalizado MCP: podrás consultar clientes, facturación, presupuestos, leads, tareas, tickets, correos y agenda, y pedirle que cree facturas, presupuestos, tareas o notas con tu propio usuario y permisos.
        </p>
        {error && <div className="alert alert-danger py-2">{error}</div>}
        {loading && !data ? (
          <div className="small text-muted">Cargando…</div>
        ) : data && (
          <>
            <div className="mb-2">
              <label className="form-label small fw-semibold mb-1">URL del conector</label>
              <div className="input-group input-group-sm">
                <input className="form-control" readOnly value={data.url} onFocus={(e) => e.target.select()} />
                <button type="button" className="btn btn-outline-secondary" onClick={copyUrl}>{copied ? 'Copiada' : 'Copiar'}</button>
              </div>
            </div>
            <button type="button" className="btn btn-link btn-sm p-0 align-self-start mb-2" onClick={() => setShowHelp((v) => !v)}>
              {showHelp ? 'Ocultar instrucciones' : 'Cómo añadirlo en Claude'}
            </button>
            {showHelp && (
              <ol className="small mb-3 ps-3">
                <li>En claude.ai entra en <strong>Configuración → Conectores</strong> y pulsa <strong>Añadir conector personalizado</strong>.</li>
                <li>Nombre: <em>Automationia</em>. URL: pega la de arriba. Deja la autenticación en OAuth (no hace falta client ID ni secreto).</li>
                <li>Pulsa <strong>Conectar</strong>: se abre la pantalla de la app, entra con tu email y contraseña y elige si Claude puede solo consultar o también crear y modificar.</li>
                <li>Al volver a Claude, activa el conector en el menú de herramientas de la conversación. En la app móvil aparece automáticamente una vez añadido en la web.</li>
                <li>Claude te pedirá confirmación antes de cada acción (puedes marcar «Permitir siempre» para las consultas). Aquí abajo verás las conexiones activas y podrás revocarlas.</li>
              </ol>
            )}

            <div className="small fw-semibold mb-1">Conexiones activas</div>
            {connections.length === 0 ? (
              <div className="small text-muted mb-2">Ninguna todavía.</div>
            ) : (
              <div className="table-responsive mb-2">
                <table className="table table-sm align-middle mb-0 small">
                  <thead>
                    <tr><th>Cliente</th><th>Usuario</th><th>Permisos</th><th>Último uso</th><th className="text-end">Acciones</th></tr>
                  </thead>
                  <tbody>
                    {connections.map((c) => (
                      <tr key={c.grantId}>
                        <td>{c.client}</td>
                        <td>{c.user}</td>
                        <td>{c.scope.includes('escritura') ? 'Lectura y escritura' : 'Solo lectura'}</td>
                        <td title={`Conectado el ${fmt(c.createdAt)} · ${c.calls} acciones`}>{fmt(c.lastUsedAt || c.createdAt)}</td>
                        <td className="text-end"><button type="button" className="btn btn-outline-danger btn-sm" onClick={() => revoke(c)}>Revocar</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activity.length > 0 && (
              <>
                <button type="button" className="btn btn-link btn-sm p-0 align-self-start" onClick={() => setShowActivity((v) => !v)}>
                  {showActivity ? 'Ocultar actividad' : `Ver últimas acciones (${activity.length})`}
                </button>
                {showActivity && (
                  <div className="table-responsive mt-2">
                    <table className="table table-sm mb-0 small">
                      <thead><tr><th>Cuándo</th><th>Usuario</th><th>Herramienta</th><th>Resultado</th></tr></thead>
                      <tbody>
                        {activity.map((a) => (
                          <tr key={a.id}>
                            <td className="text-nowrap">{fmt(a.created_at)}</td>
                            <td>{a.user_email || '—'}</td>
                            <td><code>{a.tool}</code></td>
                            <td>{a.ok ? <span className="text-success">OK</span> : <span className="text-danger" title={a.error || ''}>Error</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </>
        )}
        <button className="btn btn-outline-secondary btn-sm mt-2 align-self-start" onClick={load} disabled={loading}>Actualizar</button>
      </div>
    </div>
  );
};

export default ClaudeConnectorCard;
